// A history import's upload, straight to the bucket (spec
// 2026-10-29-garmin-history-import, D6): the LiveView starts a multipart
// upload and answers with a presigned URL per part; the browser PUTs the
// parts, two at a time, keeping each answer's ETag; then the LiveView
// completes the upload. One zip goes up as it is; anything else is first
// wrapped into one stored zip (`zipFiles`, activity file import D11). The
// hook (HistoryUploadHook) wires this to the page; everything it does is
// injected, so the logic is tested without a DOM.

export interface StartReply {
  ok?: boolean
  error?: string
  import_id?: number
  part_bytes?: number
  part_urls?: string[]
}

export interface Reply {
  ok?: boolean
  error?: string
}

export interface UploadedPart {
  part_number: number
  etag: string
}

export interface UploadPartsOptions {
  file: Blob
  partBytes: number
  partUrls: string[]
  signal: AbortSignal
  onProgress: (uploadedBytes: number) => void
}

export type PushEvent = (event: string, payload: Record<string, unknown>) => Promise<unknown>

export interface HistoryUploadDeps {
  pushEvent: PushEvent
  sleep: (ms: number) => Promise<void>
  uploadParts: (options: UploadPartsOptions) => Promise<UploadedPart[]>
}

export const maxCompleteAttempts = 5
const completeRetryMs = 2000

interface CurrentUpload {
  importId: number
  controller: AbortController
  // The whole percent last sent.
  percent: number
}

// What goes up: its exact size, known before anything is read, so the
// LiveView can refuse it (over 10 GB, an import running) first, and how to
// make the Blob that's uploaded.
export interface UploadSource {
  size: number
  build: (signal: AbortSignal) => Promise<Blob>
}

export interface HistoryUploader {
  start: (source: UploadSource) => Promise<void>
  abort: (importId: number) => void
  stop: () => void
  reportState: () => void
}

export function createHistoryUploader(deps: HistoryUploadDeps): HistoryUploader {
  let current: CurrentUpload | null = null

  function stop() {
    if (current) {
      current.controller.abort()
      current = null
    }
  }

  // The LiveView asks to stop an upload it cancelled. Only that import's:
  // a new upload may have started meanwhile.
  function abort(importId: number) {
    if (current && current.importId === importId) {
      stop()
    }
  }

  function finish(upload: CurrentUpload) {
    if (current === upload) {
      current = null
    }
  }

  async function start(source: UploadSource) {
    // Picking the zip again starts over (D19).
    stop()

    let reply: StartReply
    try {
      reply = (await deps.pushEvent('history_upload_start', { size: source.size })) as StartReply
    } catch (_error) {
      return
    }

    if (!reply || !reply.ok || reply.import_id == null || !reply.part_urls || !reply.part_bytes) {
      return
    }

    const upload: CurrentUpload = {
      importId: reply.import_id,
      controller: new AbortController(),
      percent: 0,
    }
    current = upload

    // A file that can't be read (removed since it was picked, a folder) stops
    // the upload: the LiveView cancels the import and says so.
    let file: Blob
    try {
      file = await source.build(upload.controller.signal)
      if (file.size !== source.size) {
        throw new Error('the built upload has another size')
      }
    } catch (_error) {
      if (upload.controller.signal.aborted) {
        return
      }

      finish(upload)
      upload.controller.abort()
      await pushQuietly('history_upload_unreadable', { import_id: upload.importId })
      return
    }

    if (upload.controller.signal.aborted) {
      return
    }

    let parts: UploadedPart[]
    try {
      parts = await deps.uploadParts({
        file,
        partBytes: reply.part_bytes,
        partUrls: reply.part_urls,
        signal: upload.controller.signal,
        onProgress: progressReporter(upload, file.size),
      })
    } catch (_error) {
      if (upload.controller.signal.aborted) {
        return
      }

      finish(upload)
      // Stops the parts still uploading: the upload can't complete.
      upload.controller.abort()
      await pushQuietly('history_upload_failed', { import_id: upload.importId })
      return
    }

    if (upload.controller.signal.aborted) {
      return
    }

    await complete(upload, parts)
    finish(upload)
  }

  // Sends the progress as a whole percent, only when it changes.
  function progressReporter(upload: CurrentUpload, size: number) {
    return function reportProgress(uploadedBytes: number) {
      if (upload.controller.signal.aborted || size <= 0) {
        return
      }

      const percent = Math.min(100, Math.floor((uploadedBytes / size) * 100))
      if (percent === upload.percent) {
        return
      }

      upload.percent = percent
      sendProgress(upload)
    }
  }

  function sendProgress(upload: CurrentUpload) {
    pushQuietly('history_upload_progress', { import_id: upload.importId, percent: upload.percent })
  }

  // A LiveView that remounted (a reconnect) knows nothing of this tab's
  // upload until told: its card would say the upload didn't finish. Sends
  // the running upload's progress again, through the completion's retries.
  function reportState() {
    if (current) {
      sendProgress(current)
    }
  }

  // `storage_failed` leaves the import uploading for another try, and a push
  // that fails (no connection) may not have reached the server: both are sent
  // again. Any other answer is final. When the tries run out, the LiveView is
  // told, so the card stops showing the upload as running.
  async function complete(upload: CurrentUpload, parts: UploadedPart[]) {
    for (let attempt = 1; attempt <= maxCompleteAttempts; attempt++) {
      const retry = await completeOnce(upload, parts)
      if (!retry) {
        return
      }

      if (attempt < maxCompleteAttempts) {
        await deps.sleep(completeRetryMs * attempt)
      }

      if (upload.controller.signal.aborted) {
        return
      }
    }

    await pushQuietly('history_upload_stalled', { import_id: upload.importId })
  }

  // Whether the completion should be sent again.
  async function completeOnce(upload: CurrentUpload, parts: UploadedPart[]): Promise<boolean> {
    let reply: Reply
    try {
      reply = (await deps.pushEvent('history_upload_complete', {
        import_id: upload.importId,
        parts,
      })) as Reply
    } catch (_error) {
      return true
    }

    return Boolean(reply && reply.error === 'storage_failed')
  }

  // A push whose failure changes nothing: the LiveView remounts on rejoin.
  async function pushQuietly(event: string, payload: Record<string, unknown>) {
    try {
      await deps.pushEvent(event, payload)
    } catch (_error) {
      return
    }
  }

  return { start, abort, stop, reportState }
}

export type PutPart = (
  url: string,
  body: Blob,
  signal: AbortSignal,
  onProgress: (loadedBytes: number) => void,
) => Promise<string>

const partAttempts = 3
const partRetryMs = 1000
export const partConcurrency = 2

export interface UploadPartsDeps {
  putPart: PutPart
  sleep: (ms: number) => Promise<void>
}

// Uploads every part, `partConcurrency` at a time, each tried a few times.
// Rejects with the first part that fails every try, or when aborted.
export function uploadParts(
  options: UploadPartsOptions,
  deps: UploadPartsDeps,
): Promise<UploadedPart[]> {
  const loaded: number[] = options.partUrls.map(function noBytes() {
    return 0
  })
  const results: UploadedPart[] = []
  let next = 0

  function reportProgress() {
    const total = loaded.reduce(function sum(acc, bytes) {
      return acc + bytes
    }, 0)
    options.onProgress(total)
  }

  async function uploadPart(index: number) {
    const url = options.partUrls[index]
    if (url === undefined) {
      throw new Error(`no URL for part ${index + 1}`)
    }

    const start = index * options.partBytes
    const body = options.file.slice(start, start + options.partBytes)

    for (let attempt = 1; ; attempt++) {
      if (options.signal.aborted) {
        throw new Error('aborted')
      }

      try {
        const etag = await deps.putPart(
          url,
          body,
          options.signal,
          function onPartProgress(bytes: number) {
            loaded[index] = bytes
            reportProgress()
          },
        )
        loaded[index] = body.size
        reportProgress()
        results.push({ part_number: index + 1, etag })
        return
      } catch (error) {
        loaded[index] = 0
        if (options.signal.aborted || attempt >= partAttempts) {
          throw error
        }
        await deps.sleep(partRetryMs * attempt)
      }
    }
  }

  async function worker() {
    while (next < options.partUrls.length) {
      const index = next
      next += 1
      await uploadPart(index)
    }
  }

  const workers = []
  for (let i = 0; i < Math.min(partConcurrency, options.partUrls.length); i++) {
    workers.push(worker())
  }

  return Promise.all(workers).then(function sortedParts() {
    return results.sort(function byPartNumber(a, b) {
      return a.part_number - b.part_number
    })
  })
}

// A part's PUT with XMLHttpRequest, for its upload progress. The bucket must
// expose `ETag` to the page (its CORS rules).
export function xhrPutPart(
  url: string,
  body: Blob,
  signal: AbortSignal,
  onProgress: (loadedBytes: number) => void,
): Promise<string> {
  return new Promise(function putPart(resolve, reject) {
    const xhr = new XMLHttpRequest()

    function onAbort() {
      xhr.abort()
    }

    xhr.open('PUT', url)
    xhr.upload.onprogress = function onUploadProgress(event) {
      onProgress(event.loaded)
    }
    xhr.onload = function onLoad() {
      signal.removeEventListener('abort', onAbort)
      const etag = xhr.getResponseHeader('ETag')

      if (xhr.status >= 200 && xhr.status < 300 && etag) {
        resolve(etag)
      } else {
        reject(new Error(`part upload failed: ${xhr.status}`))
      }
    }
    xhr.onerror = function onError() {
      signal.removeEventListener('abort', onAbort)
      reject(new Error('part upload failed'))
    }
    xhr.onabort = function onXhrAbort() {
      signal.removeEventListener('abort', onAbort)
      reject(new Error('aborted'))
    }

    signal.addEventListener('abort', onAbort)
    xhr.send(body)
  })
}

export function sleep(ms: number): Promise<void> {
  return new Promise(function wait(resolve) {
    setTimeout(resolve, ms)
  })
}

// -- what the rider picked, as one upload (activity file import, D11) --

// A Blob that goes up as it is.
export function blobSource(file: Blob): UploadSource {
  return {
    size: file.size,
    build: async function asItIs() {
      return file
    },
  }
}

// One zip goes up as it is; anything else, several files or a mix, is
// wrapped into one stored zip, in which a zip is a folder (D12).
export function uploadSource(files: File[]): UploadSource {
  const only = files[0]
  if (files.length === 1 && only && isZip(only)) {
    return blobSource(only)
  }

  return zipFiles(files)
}

function isZip(file: File): boolean {
  return file.name.toLowerCase().endsWith('.zip')
}

const u16Max = 0xffff
const u32Max = 0xffffffff
const localHeaderBytes = 30
const centralHeaderBytes = 46
const endRecordBytes = 22
const zip64EndRecordBytes = 56
const zip64LocatorBytes = 20
// Bit 11: the names are UTF-8.
const utf8Flag = 0x0800
// Every entry's date and time: 1980-01-01 00:00, the first a zip can hold.
// Nothing reads them, and they keep the wrapper's bytes the same everywhere.
const dosTime = 0
const dosDate = (0 << 9) | (1 << 5) | 1

interface ZipEntry {
  file: Blob
  name: Uint8Array
  offset: number
  // Whether the size, or the offset, needs ZIP64's 64 bits.
  bigSize: boolean
  bigOffset: boolean
}

// The files as one stored (uncompressed) zip, joined as Blobs, so none is
// held in memory: only the CRC-32 reads them, when it's built. Its size is
// known before then. ZIP64 where a size, an offset or the count needs it.
export function zipFiles(files: Blob[]): UploadSource {
  const encoder = new TextEncoder()
  const entries: ZipEntry[] = []
  let offset = 0

  files.forEach(function addEntry(file, index) {
    const name = encoder.encode(entryName(file, index))
    const bigSize = file.size >= u32Max
    const entry = { file, name, offset, bigSize, bigOffset: offset >= u32Max }
    entries.push(entry)
    offset += localHeaderBytes + name.length + localExtraBytes(entry) + file.size
  })

  const directoryOffset = offset
  const directorySize = entries.reduce(function sum(acc, entry) {
    return acc + centralHeaderBytes + entry.name.length + centralExtraBytes(entry)
  }, 0)
  const zip64 = entries.length >= u16Max || directoryOffset >= u32Max || directorySize >= u32Max
  const endBytes = (zip64 ? zip64EndRecordBytes + zip64LocatorBytes : 0) + endRecordBytes

  return {
    size: directoryOffset + directorySize + endBytes,
    build: async function buildZip(signal: AbortSignal) {
      const parts: BlobPart[] = []
      const centrals: BlobPart[] = []

      for (const entry of entries) {
        const crc = await crc32(entry.file, signal)
        parts.push(localHeader(entry, crc), entry.file)
        centrals.push(centralHeader(entry, crc))
      }

      parts.push(...centrals)
      if (zip64) {
        const recordOffset = directoryOffset + directorySize
        parts.push(zip64EndRecord(entries.length, directorySize, directoryOffset))
        parts.push(zip64Locator(recordOffset))
      }
      parts.push(endRecord(entries.length, directorySize, directoryOffset, zip64))

      return new Blob(parts, { type: 'application/zip' })
    },
  }
}

// A File's own name; a Blob has none.
function entryName(file: Blob, index: number): string {
  if (file instanceof File && file.name !== '') {
    return file.name
  }

  return `file-${index + 1}`
}

function localExtraBytes(entry: ZipEntry): number {
  return entry.bigSize ? 4 + 16 : 0
}

// Only the values that don't fit their 32 bits, in the order size,
// compressed size, offset.
function centralExtraBytes(entry: ZipEntry): number {
  const values = (entry.bigSize ? 2 : 0) + (entry.bigOffset ? 1 : 0)
  return values > 0 ? 4 + values * 8 : 0
}

function versionNeeded(entry: ZipEntry): number {
  return entry.bigSize || entry.bigOffset ? 45 : 20
}

function localHeader(entry: ZipEntry, crc: number): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(localHeaderBytes + entry.name.length + localExtraBytes(entry))
  const view = new DataView(bytes.buffer)
  const size = entry.bigSize ? u32Max : entry.file.size

  view.setUint32(0, 0x04034b50, true)
  view.setUint16(4, versionNeeded(entry), true)
  view.setUint16(6, utf8Flag, true)
  view.setUint16(8, 0, true)
  view.setUint16(10, dosTime, true)
  view.setUint16(12, dosDate, true)
  view.setUint32(14, crc, true)
  view.setUint32(18, size, true)
  view.setUint32(22, size, true)
  view.setUint16(26, entry.name.length, true)
  view.setUint16(28, localExtraBytes(entry), true)
  bytes.set(entry.name, localHeaderBytes)

  if (entry.bigSize) {
    const extra = localHeaderBytes + entry.name.length
    view.setUint16(extra, 0x0001, true)
    view.setUint16(extra + 2, 16, true)
    view.setBigUint64(extra + 4, BigInt(entry.file.size), true)
    view.setBigUint64(extra + 12, BigInt(entry.file.size), true)
  }

  return bytes
}

function centralHeader(entry: ZipEntry, crc: number): Uint8Array<ArrayBuffer> {
  const extraBytes = centralExtraBytes(entry)
  const bytes = new Uint8Array(centralHeaderBytes + entry.name.length + extraBytes)
  const view = new DataView(bytes.buffer)
  const size = entry.bigSize ? u32Max : entry.file.size

  view.setUint32(0, 0x02014b50, true)
  view.setUint16(4, versionNeeded(entry), true)
  view.setUint16(6, versionNeeded(entry), true)
  view.setUint16(8, utf8Flag, true)
  view.setUint16(10, 0, true)
  view.setUint16(12, dosTime, true)
  view.setUint16(14, dosDate, true)
  view.setUint32(16, crc, true)
  view.setUint32(20, size, true)
  view.setUint32(24, size, true)
  view.setUint16(28, entry.name.length, true)
  view.setUint16(30, extraBytes, true)
  // The comment's length, the disk, the internal and external attributes:
  // all 0.
  view.setUint32(42, entry.bigOffset ? u32Max : entry.offset, true)
  bytes.set(entry.name, centralHeaderBytes)

  if (extraBytes > 0) {
    let at = centralHeaderBytes + entry.name.length
    view.setUint16(at, 0x0001, true)
    view.setUint16(at + 2, extraBytes - 4, true)
    at += 4
    if (entry.bigSize) {
      view.setBigUint64(at, BigInt(entry.file.size), true)
      view.setBigUint64(at + 8, BigInt(entry.file.size), true)
      at += 16
    }
    if (entry.bigOffset) {
      view.setBigUint64(at, BigInt(entry.offset), true)
    }
  }

  return bytes
}

function zip64EndRecord(count: number, directorySize: number, directoryOffset: number) {
  const bytes = new Uint8Array(zip64EndRecordBytes)
  const view = new DataView(bytes.buffer)

  view.setUint32(0, 0x06064b50, true)
  // The record's size, less its first 12 bytes.
  view.setBigUint64(4, BigInt(zip64EndRecordBytes - 12), true)
  view.setUint16(12, 45, true)
  view.setUint16(14, 45, true)
  view.setBigUint64(24, BigInt(count), true)
  view.setBigUint64(32, BigInt(count), true)
  view.setBigUint64(40, BigInt(directorySize), true)
  view.setBigUint64(48, BigInt(directoryOffset), true)

  return bytes
}

function zip64Locator(recordOffset: number) {
  const bytes = new Uint8Array(zip64LocatorBytes)
  const view = new DataView(bytes.buffer)

  view.setUint32(0, 0x07064b50, true)
  view.setBigUint64(8, BigInt(recordOffset), true)
  view.setUint32(16, 1, true)

  return bytes
}

// With ZIP64, every value says to read the ZIP64 record instead.
function endRecord(count: number, directorySize: number, directoryOffset: number, zip64: boolean) {
  const bytes = new Uint8Array(endRecordBytes)
  const view = new DataView(bytes.buffer)

  view.setUint32(0, 0x06054b50, true)
  view.setUint16(8, zip64 ? u16Max : count, true)
  view.setUint16(10, zip64 ? u16Max : count, true)
  view.setUint32(12, zip64 ? u32Max : directorySize, true)
  view.setUint32(16, zip64 ? u32Max : directoryOffset, true)

  return bytes
}

const crcChunkBytes = 8 * 1024 * 1024

let crcTable: Uint32Array | null = null

function crcTableOnce(): Uint32Array {
  if (crcTable) {
    return crcTable
  }

  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[n] = c >>> 0
  }
  crcTable = table
  return table
}

// The file's CRC-32, read a chunk at a time.
export async function crc32(file: Blob, signal: AbortSignal): Promise<number> {
  const table = crcTableOnce()
  let crc = 0xffffffff

  for (let start = 0; start < file.size; start += crcChunkBytes) {
    if (signal.aborted) {
      throw new Error('aborted')
    }

    const chunk = new Uint8Array(await file.slice(start, start + crcChunkBytes).arrayBuffer())
    for (let i = 0; i < chunk.length; i++) {
      crc = table[(crc ^ chunk[i]!) & 0xff]! ^ (crc >>> 8)
    }
  }

  return (crc ^ 0xffffffff) >>> 0
}
